// src/components/CartItem.tsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CartItem as CartItemType } from '../types';
import { PriceDisplay } from './PriceDisplay';

const attributeLabels: Record<string, string> = {
    size: 'Размер',
    volume: 'Объем',
    weight: 'Вес',
    dimensions: 'Габариты',
    other: 'Другое'
};

interface CartItemProps {
    item: CartItemType;
    onUpdateQuantity: (id: string, quantity: number) => Promise<void>;
    onRemove: (id: string) => Promise<void>;
}

export const CartItem: React.FC<CartItemProps> = ({
                                                      item,
                                                      onUpdateQuantity,
                                                      onRemove,
                                                  }) => {
    const navigate = useNavigate();
    const product = item.product;
    const variant = item.variant;

    if (!product || !variant) return null;

    const mainImage = product.images && product.images.length > 0 ? product.images[0] : 'https://via.placeholder.com/100';
    const unitPrice = variant.price || 0;
    const totalPrice = unitPrice * item.quantity;

    const handleProductClick = () => {
        if (product && product.id) {
            navigate(`/product/${product.id}`, {
                state: { selectedVariantId: variant.id }
            });
        }
    };

    const handleUpdate = (newQuantity: number) => {
        if (newQuantity === item.quantity) return;
        onUpdateQuantity(item.id, newQuantity);
    };

    const handleRemove = () => {
        onRemove(item.id);
    };

    const getAttributesWithLabels = (): { label: string; value: string }[] => {
        if (!variant.attributes) return [];

        const result: { label: string; value: string }[] = [];

        try {
            const attrs = variant.attributes;

            if (Array.isArray(attrs)) {
                for (const attr of attrs) {
                    if (attr.type && attr.value) {
                        const label = attributeLabels[attr.type] || attr.type;
                        let displayValue = '';

                        switch (attr.type) {
                            case 'size':
                                displayValue = `${attr.value}`;
                                break;
                            case 'volume':
                                displayValue = `${attr.value}${attr.unit ? ` ${attr.unit}` : ''}`;
                                break;
                            case 'weight':
                                displayValue = `${attr.value}${attr.unit ? ` ${attr.unit}` : ''}`;
                                break;
                            case 'dimensions':
                                displayValue = `${attr.value}${attr.unit ? ` ${attr.unit}` : ''}`;
                                break;
                            case 'other':
                                displayValue = `${attr.name || 'Другое'}: ${attr.value}`;
                                break;
                            default:
                                displayValue = attr.value;
                        }

                        if (displayValue) {
                            result.push({ label, value: displayValue });
                        }
                    }
                }
            }
        } catch (e) {
            console.error('Error parsing attributes:', e);
        }

        return result;
    };

    const getCharacteristicString = (): string => {
        const parts: string[] = [];

        if (product.color && product.color !== 'null' && product.color !== 'undefined') {
            parts.push(product.color);
        }

        const attributesWithLabels = getAttributesWithLabels();
        for (const attr of attributesWithLabels) {
            parts.push(`${attr.label}: ${attr.value}`);
        }

        if (parts.length === 0) {
            if (variant.size_or_volume) parts.push(`Размер: ${variant.size_or_volume}`);
            if (variant.material) parts.push(`Материал: ${variant.material}`);
        }

        if (parts.length === 0 && variant.instance_id) {
            return `Артикул: ${variant.instance_id}`;
        }

        return parts.join(' · ');
    };

    const characteristic = getCharacteristicString();

    return (
        <div className="cart-item">
            <div className="cart-item_header">
            {/* Левая часть: картинка */}
            <img
                src={mainImage}
                alt={product.name}
                className="cart-item-image"
                onClick={handleProductClick}
            />

            {/* Правая часть: вся информация */}
            <div className="cart-item-info">
                {/* Название товара */}
                <h3 className="cart-item-name" onClick={handleProductClick}>
                    {product.name}
                </h3>

                {/* Характеристики */}
                {characteristic && (
                    <p className="cart-item-characteristic">
                        {characteristic}
                    </p>
                )}

                {/* Цена за единицу
                <div className="cart-item-price">
                    <PriceDisplay priceInTJS={unitPrice} size={14} buttonSize="small" showButtonFlag={false} />
                </div> */}



            </div>
           </div>

            {/* Общая сумма */}
            <div className="cart-item-total">
                <PriceDisplay priceInTJS={totalPrice} size={16} buttonSize="small" showButtonFlag={false} />
            </div>

            {/* Нижняя строка: количество и удаление */}
            <div className="cart-item-actions">


                <div className="quantity-control">
                    <button
                        onClick={() => handleUpdate(item.quantity - 1)}
                        className="quantity-btn"
                        disabled={item.quantity <= 1}
                    >
                        -
                    </button>
                    <span className="quantity-value">{item.quantity}</span>
                    <button
                        onClick={() => handleUpdate(item.quantity + 1)}
                        className="quantity-btn"
                    >
                        +
                    </button>
                </div>
                <button onClick={handleRemove} className="remove-btn">
                    🗑️ Удалить
                </button>
            </div>
        </div>
    );
};